import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  SystemProfile, 
  RecommendedSettings,
  computeRecommendations, 
  diffSettings,
  BUILT_IN_PRESETS,
  SETTINGS_LABELS,
  formatSettingValue 
} from '@/lib/solark-recommendations';

export type WizardPhase = 'discovery' | 'snapshot' | 'configure' | 'diff' | 'apply' | 'verify';

interface InverterInfo {
  sn: string;
  alias: string;
  status: number;
  plantId: string;
  plantName: string;
  model: string;
  ratePower: number;
  role: 'primary' | 'secondary' | 'standalone';
  isOnline: boolean;
}

interface SettingsDiff {
  field: string;
  current: unknown;
  recommended: unknown;
}

interface InverterSettingsState {
  sn: string;
  alias: string;
  role: 'primary' | 'secondary' | 'standalone';
  currentSettings: Record<string, unknown> | null;
  categorizedSettings: Record<string, Record<string, unknown>> | null;
  recommendedSettings: RecommendedSettings | null;
  diff: SettingsDiff[];
  snapshotId: string | null;
  writeResult: {
    success: boolean;
    fieldsWritten: string[];
    verificationPassed: boolean;
  } | null;
}

interface UseConfigWizardProps {
  systemId: string;
  onComplete?: () => void;
}

export function useConfigWizard({ systemId, onComplete }: UseConfigWizardProps) {
  const [phase, setPhase] = useState<WizardPhase>('discovery');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Discovery state
  const [inverters, setInverters] = useState<InverterInfo[]>([]);
  const [selectedInverters, setSelectedInverters] = useState<string[]>([]);
  
  // Settings state per inverter
  const [inverterSettings, setInverterSettings] = useState<Map<string, InverterSettingsState>>(new Map());
  
  // User profile for recommendations
  const [systemProfile, setSystemProfile] = useState<SystemProfile>({
    batteryType: 'lithium',
    batteryCapacityAh: 100,
    batteryVoltage: 48,
    batteryCount: 1,
    hasGenerator: false,
    generatorAutoStart: false,
    gridTied: false,
    zeroExport: true,
    reservePercent: 20,
    prioritizeSelfConsumption: true,
  });
  
  // Selected preset
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  /**
   * Phase 1: Discovery - List available inverters
   */
  const discoverInverters = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: funcError } = await supabase.functions.invoke('solark-list-inverters', {
        body: { systemId },
      });
      
      if (funcError) throw funcError;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to list inverters');
      }
      
      setInverters(data.inverters);
      
      // Auto-select all inverters
      setSelectedInverters(data.inverters.map((inv: InverterInfo) => inv.sn));
      
      // Initialize settings state for each inverter
      const settingsMap = new Map<string, InverterSettingsState>();
      for (const inv of data.inverters) {
        settingsMap.set(inv.sn, {
          sn: inv.sn,
          alias: inv.alias,
          role: inv.role,
          currentSettings: null,
          categorizedSettings: null,
          recommendedSettings: null,
          diff: [],
          snapshotId: null,
          writeResult: null,
        });
      }
      setInverterSettings(settingsMap);
      
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  /**
   * Phase 2: Snapshot - Read current settings for all selected inverters
   */
  const takeSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedSettings = new Map(inverterSettings);
      
      for (const sn of selectedInverters) {
        const { data, error: funcError } = await supabase.functions.invoke('solark-read-settings', {
          body: { 
            systemId, 
            deviceSn: sn,
            saveSnapshot: true,
            snapshotType: 'baseline',
          },
        });
        
        if (funcError) throw funcError;
        
        if (!data.success) {
          throw new Error(data.error || `Failed to read settings for ${sn}`);
        }
        
        const current = updatedSettings.get(sn);
        if (current) {
          updatedSettings.set(sn, {
            ...current,
            currentSettings: data.settings,
            categorizedSettings: data.categorized,
            snapshotId: data.snapshotId,
          });
        }
      }
      
      setInverterSettings(updatedSettings);
      setPhase('configure');
      
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [systemId, selectedInverters, inverterSettings]);

  /**
   * Phase 3: Compute recommendations based on profile
   */
  const computeRecommendedSettings = useCallback(() => {
    const recommendations = computeRecommendations(systemProfile);
    
    const updatedSettings = new Map(inverterSettings);
    
    for (const [sn, state] of updatedSettings) {
      if (!selectedInverters.includes(sn)) continue;
      
      // Compute diff between current and recommended
      const currentSettings = state.currentSettings || {};
      const diff = diffSettings(currentSettings, recommendations as unknown as Record<string, unknown>);
      
      updatedSettings.set(sn, {
        ...state,
        recommendedSettings: recommendations,
        diff,
      });
    }
    
    setInverterSettings(updatedSettings);
    setPhase('diff');
  }, [systemProfile, inverterSettings, selectedInverters]);

  /**
   * Apply a built-in preset
   */
  const applyPreset = useCallback((presetKey: string) => {
    const preset = BUILT_IN_PRESETS[presetKey as keyof typeof BUILT_IN_PRESETS];
    if (!preset) return;
    
    setSelectedPreset(presetKey);
    setSystemProfile(prev => ({
      ...prev,
      ...preset.profile,
    }));
  }, []);

  /**
   * Phase 4: Apply settings to inverters
   */
  const applySettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedSettings = new Map(inverterSettings);
      
      // Sort inverters: primary first, then secondary
      const sortedSns = [...selectedInverters].sort((a, b) => {
        const aState = inverterSettings.get(a);
        const bState = inverterSettings.get(b);
        if (aState?.role === 'primary') return -1;
        if (bState?.role === 'primary') return 1;
        return 0;
      });
      
      for (const sn of sortedSns) {
        const state = inverterSettings.get(sn);
        if (!state?.recommendedSettings) continue;
        
        // Only send fields that are different
        const settingsToWrite: Record<string, unknown> = {};
        for (const diff of state.diff) {
          settingsToWrite[diff.field] = diff.recommended;
        }
        
        if (Object.keys(settingsToWrite).length === 0) {
          // No changes needed for this inverter
          updatedSettings.set(sn, {
            ...state,
            writeResult: {
              success: true,
              fieldsWritten: [],
              verificationPassed: true,
            },
          });
          continue;
        }
        
        const { data, error: funcError } = await supabase.functions.invoke('solark-write-settings', {
          body: {
            systemId,
            deviceSn: sn,
            settings: settingsToWrite,
          },
        });
        
        if (funcError) throw funcError;
        
        updatedSettings.set(sn, {
          ...state,
          writeResult: {
            success: data.success,
            fieldsWritten: data.fieldsWritten || [],
            verificationPassed: data.verificationPassed ?? false,
          },
        });
        
        // Add delay between inverters
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      setInverterSettings(updatedSettings);
      setPhase('verify');
      
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [systemId, selectedInverters, inverterSettings]);

  /**
   * Get overall verification status
   */
  const getVerificationStatus = useCallback(() => {
    let allSuccess = true;
    let allVerified = true;
    let totalFields = 0;
    let writtenFields = 0;
    
    for (const [sn, state] of inverterSettings) {
      if (!selectedInverters.includes(sn)) continue;
      
      if (state.writeResult) {
        if (!state.writeResult.success) allSuccess = false;
        if (!state.writeResult.verificationPassed) allVerified = false;
        totalFields += state.diff.length;
        writtenFields += state.writeResult.fieldsWritten.length;
      }
    }
    
    return { allSuccess, allVerified, totalFields, writtenFields };
  }, [inverterSettings, selectedInverters]);

  /**
   * Reset wizard to start
   */
  const reset = useCallback(() => {
    setPhase('discovery');
    setInverters([]);
    setSelectedInverters([]);
    setInverterSettings(new Map());
    setError(null);
    setSelectedPreset(null);
  }, []);

  /**
   * Move to next phase
   */
  const nextPhase = useCallback(() => {
    switch (phase) {
      case 'discovery':
        if (selectedInverters.length > 0) {
          setPhase('snapshot');
          takeSnapshot();
        }
        break;
      case 'snapshot':
        // Handled by takeSnapshot
        break;
      case 'configure':
        computeRecommendedSettings();
        break;
      case 'diff':
        setPhase('apply');
        applySettings();
        break;
      case 'apply':
        // Handled by applySettings
        break;
      case 'verify':
        onComplete?.();
        break;
    }
  }, [phase, selectedInverters, takeSnapshot, computeRecommendedSettings, applySettings, onComplete]);

  // Auto-discover on mount
  useEffect(() => {
    if (systemId && phase === 'discovery' && inverters.length === 0) {
      discoverInverters();
    }
  }, [systemId, phase, inverters.length, discoverInverters]);

  return {
    // State
    phase,
    loading,
    error,
    inverters,
    selectedInverters,
    inverterSettings,
    systemProfile,
    selectedPreset,
    
    // Actions
    setPhase,
    setSelectedInverters,
    setSystemProfile,
    applyPreset,
    nextPhase,
    reset,
    discoverInverters,
    takeSnapshot,
    computeRecommendedSettings,
    applySettings,
    getVerificationStatus,
    
    // Helpers
    BUILT_IN_PRESETS,
    SETTINGS_LABELS,
    formatSettingValue,
  };
}

export type UseConfigWizardReturn = ReturnType<typeof useConfigWizard>;
